/**
 * Apply Button Component
 * Large apply button with tracking and state management
 */

import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { CaretDown } from '@phosphor-icons/react';
import { trackJobApply } from '../../../../../utils/analytics/analytics';
import { useToast } from '../../../../../contexts/ToastContext';
import type { DisplayJob } from '../../../../../types';
import { Msg, useFormatMessage } from '../../../../../i18n-review/client/Msg';

export interface ApplyButtonProps {
  applyUrl: string;
  isApplied: boolean;
  displayJob: DisplayJob | null;
  applyBtnStyle: React.CSSProperties;
  applyBtnHoverStyle: React.CSSProperties;
  effectiveBrandColour: string;
  onSetApplied: (job: any, applied: boolean) => Promise<void>;
  /** Tighter spacing when placed high on the page (above the fold). */
  compact?: boolean;
  /** Second CTA after long content — uses a div wrapper so we do not duplicate a navigation landmark. */
  isSecondary?: boolean;
  /** When provided, renders a secondary "Tailor my CV" action below the apply link. */
  onTailorCv?: () => void;
}

export const ApplyButton: React.FC<ApplyButtonProps> = ({
  applyUrl,
  isApplied,
  displayJob,
  applyBtnStyle,
  applyBtnHoverStyle,
  effectiveBrandColour,
  onSetApplied,
  compact = false,
  isSecondary = false,
  onTailorCv,
}) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { showToast } = useToast();
  const [isTailorMenuOpen, setIsTailorMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isTailorMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsTailorMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isTailorMenuOpen]);

  const handleClick = () => {
    // Single conversion event per apply click (job_apply_click was removed as
    // a duplicate of job_apply_conversion — same click, same params).
    if (displayJob) {
      trackJobApply({
        jobId: displayJob.objectID,
        jobTitle: displayJob.normalisedTitle || displayJob.title || '',
        companyId: displayJob.companyId || '',
        source: 'apply_cta',
        locale: intl.locale,
        method: 'external_link',
      });
    }

    // TEC-509: no longer auto-marks the job applied on click — that happened silently, with no
    // feedback, and risked over-counting (a click through isn't a completed application). The
    // link already opens in a new tab (target="_blank" below), so the user stays on this page
    // and can act on the prompt; it persists (duration 0) until dismissed or acted on rather
    // than timing out unread, since they may be reading the employer's page in the other tab
    // first. Skipped once already applied — nothing new to confirm.
    if (displayJob && !isApplied) {
      const job = { ...displayJob, id: displayJob.objectID };
      showToast(
        formatMessage({
          id: 'jobDetailPage.markAppliedPrompt',
          defaultMessage: 'Opened the application in a new tab. Mark this job as applied?',
          description: 'Toast prompt shown after clicking Apply on the job detail page, asking the user to confirm marking the job as applied in their tracker',
        }),
        'info',
        0,
        {
          label: formatMessage({
            id: 'jobDetailPage.markAppliedAction',
            defaultMessage: 'Mark as Applied',
            description: 'Action button on the post-apply-click prompt toast, confirming the job should be marked applied',
          }),
          onClick: () => {
            void onSetApplied(job, true).then(() => {
              showToast(
                formatMessage({
                  id: 'hit.toast.applied',
                  defaultMessage: 'Marked as applied',
                  description: 'Success toast notification message shown when a job is marked as applied to',
                }),
                'success'
              );
            });
          },
        }
      );
    }
  };

  // With a Tailor CV option, the apply link's right corners join a chevron toggle instead of
  // being fully rounded — visually one split button, not two stacked ones.
  const applyLinkClassName =
    'px-6 text-white font-semibold shadow transition-transform duration-200 flex items-center justify-center text-center hover:scale-[1.02] active:scale-[0.98] ' +
    (onTailorCv ? 'flex-1 min-w-0 rounded-l-2xl rounded-r-none' : 'w-full rounded-2xl') +
    ' ' +
    (compact ? 'py-3.5 text-base' : 'py-4 text-lg');

  const link = (
    <a
      href={applyUrl}
      className={applyLinkClassName}
      style={applyBtnStyle}
      target="_blank"
      rel="noopener noreferrer"
      onMouseOver={effectiveBrandColour ? (e) => Object.assign(e.currentTarget.style, applyBtnHoverStyle) : undefined}
      onMouseOut={effectiveBrandColour ? (e) => Object.assign(e.currentTarget.style, applyBtnStyle) : undefined}
      onClick={handleClick}
    >
      {isApplied ? (
        <Msg
          id="jobDetailPage.applied"
          defaultMessage="Applied ✓"
          description="Button text when job has been applied to"
        />
      ) : (
        <Msg id="jobDetailPage.applyNow" defaultMessage="Apply" description="Apply now button on job detail page. Kept short (not 'Apply on Company Website') since naming the outbound destination up front reads as alarming; the description text above already sets that expectation" />
      )}
    </a>
  );

  const tailorCvMenuLabel = formatMessage({
    id: 'jobDetailPage.tailorCvCta',
    defaultMessage: 'Tailor my CV for this job',
    description: 'Secondary action below the apply button offering to tailor the user\'s CV to this job',
  });

  const splitToggle = onTailorCv ? (
    <div className="relative flex-none" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsTailorMenuOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={isTailorMenuOpen}
        aria-label={formatMessage({
          id: 'jobDetailPage.applyMoreOptions',
          defaultMessage: 'More apply options',
          description: 'Button that opens a small menu with additional apply-related actions (currently just Tailor my CV)',
        })}
        className={
          // Same brand-reactive background as the apply link (applyBtnStyle), with a thin
          // current-color divider standing in for a border between the two segments.
          'h-full px-3 rounded-r-2xl border-l flex items-center justify-center transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/50 ' +
          'border-l-current/15'
        }
        style={applyBtnStyle}
      >
        <CaretDown className={isTailorMenuOpen ? 'h-4 w-4 rotate-180 transition-transform' : 'h-4 w-4 transition-transform'} aria-hidden />
      </button>
      {isTailorMenuOpen ? (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-56 rounded-xl shadow-lg bg-jc-surface border border-jc-border z-[9999] overflow-hidden text-left"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setIsTailorMenuOpen(false);
              onTailorCv();
            }}
            className="w-full px-4 py-3 text-left text-sm font-medium text-jc-ink-secondary hover:bg-jc-elevated transition-colors cursor-pointer"
          >
            {tailorCvMenuLabel}
          </button>
        </div>
      ) : null}
    </div>
  ) : null;

  const applyRow = (
    <div className={'flex items-stretch w-full ' + (compact ? 'my-0' : 'my-6')}>
      {link}
      {splitToggle}
    </div>
  );

  if (isSecondary) {
    return <div className="w-full">{applyRow}</div>;
  }

  return (
    <nav
      id="job-apply-cta"
      aria-label={formatMessage({ id: 'jobDetailPage.applyNavigation', defaultMessage: 'Apply to job', description: 'Accessibility label for navigation to job application page' })}
    >
      {applyRow}
    </nav>
  );
};
